import { ApiSchema } from "@api-types";
import { odataList } from "services/odataApi";
import { Globals } from "types/globals";

export namespace ResultsModuleHelpers {
  export const internalResultsLink = (
    projectId: number,
    path: string,
    urlParams?: Record<string, string | number | (string | number)[] | undefined | null>,
    keepExistingParams?: boolean,
  ): string => {
    const currentSearchParams = new URLSearchParams(window.location.search);
    const mergedParams = new URLSearchParams();

    if (keepExistingParams) {
      for (const [key, value] of currentSearchParams.entries()) {
        mergedParams.set(key, value);
      }
    }

    if (urlParams) {
      for (const [key, value] of Object.entries(urlParams)) {
        if (value === undefined || value === null) {
          continue;
        }

        if (Array.isArray(value)) {
          const sanitizedArray = value
            .filter((v): v is string | number => v !== undefined && v !== null)
            .map(String);

          if (sanitizedArray.length > 0) {
            mergedParams.set(key, `[${sanitizedArray.join(",")}]`);
          }
        } else {
          mergedParams.set(key, String(value));
        }
      }
    }

    const queryString = mergedParams.toString();
    return `/results/${projectId}/${path}${queryString ? `?${queryString}` : ""}`;
  };

  export const getKeys = (dictionary: { [key in number]: boolean }) =>
    Object.keys(dictionary).map((a) => dictionary[Number(a)]);

  export const getSubKeys = (dictionary: { [key in number]: { [key in number]: boolean } }) =>
    Object.keys(Object.values(dictionary)).map((a) => dictionary[Number(a)]);

  export const getTargetsReqBuilder = (projectId: number) =>
    odataList("getTargets")
      .select("id", "question", "systemQuestionOption", "targetMode")
      .filter("projectId", "eq", projectId)
      .expandAndSelect("question", null, "questionTitle")
      .expandAndSelect("questionOption", null, "optionText")
      .expandAndSelect(
        "systemQuestionOption",
        (a) => a.expandAndSelect("systemQuestion", null, "questionLabel"),
        "answerLabel",
      );

  export const getProjectScheduleReqBuilder = (projectId: number) =>
    odataList("getProjectSchedules").filter("projectId", "eq", projectId).orderBy("createdAt");

  export const getPanelTypesReqBuilder = (projectId: number) =>
    odataList("getProjectSchedules")
      .select("id", "regionId", "isCustomerPanel")
      .filter("projectId", "eq", projectId)
      .filter("surveyRunId", "ne", null);

  export const isWaveMode = (
    project?: Pick<ApiSchema.Project, "scheduleStartDate" | "wavesRequired" | "scheduleFrequencyWeeks">,
  ) => {
    if (!project) {
      return false;
    }

    const scheduleStartDate = project.scheduleStartDate ?? null;
    const wavesRequired = !!project.wavesRequired;
    const scheduleFrequencyWeeks = project.scheduleFrequencyWeeks ?? null;

    if ((scheduleStartDate !== null && scheduleFrequencyWeeks !== null) || wavesRequired) {
      return true;
    }

    return false;
  };

  export const SystemQuestions: { [key in Globals.SystemQuestions]: string } = {
    [Globals.SystemQuestions.Generation]: "Generation",
    [Globals.SystemQuestions.HddStructure]: "Household structure",
    [Globals.SystemQuestions.Ethnicity]: "Ethnicity",
    [Globals.SystemQuestions.Region]: "Region",
    [Globals.SystemQuestions.TechAdop]: "Adoption behaviour",
    [Globals.SystemQuestions.Gender]: "Gender",
    [Globals.SystemQuestions.HddIncome]: "Household income",
    [Globals.SystemQuestions.Age]: "Age",
  };

  export const DebounceTimeMs = 1000;
}
